/* ==========================================================================
   Flavor52 Leaderboard — vanilla JS port of the React/Vite app.
   No framework: plain DOM rendering + localStorage + Supabase REST.
   Expects window.LB_ASSETS and window.LB_CONFIG to be set by the Liquid
   template before this file loads.
   ========================================================================== */
(function () {
  "use strict";

  const ASSETS = window.LB_ASSETS || {};
  const CONFIG = window.LB_CONFIG || {};

  /* ── Flavor data (mirrors src/lib/flavors.ts) ───────────────────────── */
  // Seed ratings below were adjusted at the client's explicit request during
  // testing to produce a specific top-4 for review: Oatmeal Cookie, Candied
  // Pecan, Sunday Pancakes, Grandma's Candy — with Monkey Bread at #5 and
  // Jelly Donut pushed out of the top 4. Spotlight is also temporarily
  // disabled below (see spotlightFlavorId) since it would otherwise force
  // Monkey Bread back to #1 regardless of these numbers. Real votes keep
  // landing on top of these seeds as usual — this is a starting point for
  // the test, not a permanent lock of the order.
  const FLAVORS = [
    { id: 1, name: "SUNDAY PANCAKES", combo: "Pancake + Maple Syrup", seedVotes: 450, seedRating: 8.8, seedRatingCount: 300, weekNumber: 1, image: ASSETS.sundayPancakesPod, productUrl: "https://javafactory.com/products/throwbacks-sunday-pancakes", flavorLogUrl: "https://javafactory.com/blogs/flavor-log/brewing-up-something-different" },
    { id: 2, name: "CANDIED PECAN", combo: "Toasted Pecans + Caramel", seedVotes: 623, seedRating: 8.9, seedRatingCount: 410, weekNumber: 2, image: ASSETS.candiedPecanPod, productUrl: "https://javafactory.com/products/throwbacks-candied-pecan", flavorLogUrl: "https://javafactory.com/blogs/flavor-log/flavor-log-003" },
    { id: 3, name: "JELLY DONUT", combo: "Jelly + Donut", seedVotes: 450, seedRating: 7.9, seedRatingCount: 300, weekNumber: 3, image: ASSETS.jellyDonutPod, productUrl: "https://javafactory.com/products/throwbacks-jelly-donut", flavorLogUrl: "https://javafactory.com/blogs/flavor-log/flavor-log-005" },
    { id: 4, name: "GRANDMA'S CANDY", combo: "Butterscotch + Caramel", seedVotes: 530, seedRating: 8.4, seedRatingCount: 360, weekNumber: 4, image: ASSETS.grandmasCandyPod, productUrl: "https://javafactory.com/products/throwbacks-grandmas-candy", flavorLogUrl: "https://javafactory.com/blogs/flavor-log/flavor-log-007" },
    { id: 5, name: "I LOVE ROCKY ROAD", combo: "Chocolate + Marshmallows", seedVotes: 489, seedRating: 8.0, seedRatingCount: 320, weekNumber: 5, image: ASSETS.rockyRoadPod, productUrl: "https://javafactory.com/products/throwbacks-i-love-rocky-road", flavorLogUrl: "https://javafactory.com/blogs/flavor-log/flavor-log-9" },
    { id: 6, name: "SATURDAY MORNING CARTOONS", combo: "Fruity Cereal + Cream", seedVotes: 412, seedRating: 7.7, seedRatingCount: 270, weekNumber: 6, image: ASSETS.saturdayMorningCartoonsPod, productUrl: "https://javafactory.com/products/throwbacks-saturday-morning-cartoons", flavorLogUrl: "https://javafactory.com/blogs/flavor-log/flavor-log-010" },
    { id: 7, name: "OATMEAL COOKIE", combo: "Oatmeal + Vanilla Frosting", seedVotes: 650, seedRating: 9.2, seedRatingCount: 430, weekNumber: 7, image: ASSETS.oatmealCookiePod, productUrl: "https://javafactory.com/products/throwbacks-oatmeal-cookie", flavorLogUrl: null },
    // Stays a "?" placeholder (no name/image reveal) while locked — see
    // mysteryUntilUnlocked below — client is unlocking this one manually
    // whenever they're ready, no launch date set yet.
    { id: 8, name: "MONKEY BREAD", combo: "Pastry Dough + Cinnamon Caramel", seedVotes: 410, seedRating: 8.1, seedRatingCount: 270, weekNumber: 8, image: ASSETS.monkeyBreadPod, productUrl: "https://javafactory.com/products/throwbacks-monkey-bread", flavorLogUrl: null, mysteryUntilUnlocked: true },
  ];

  // Per-flavor unlock toggles — set by the client in Shopify admin under
  // Theme Settings > Flavor52 Leaderboard (checkboxes), no code editing
  // needed. Falls back to weeks 1-2 unlocked if the setting is missing.
  const UNLOCKED_IDS = new Set(
    Array.isArray(CONFIG.unlockedFlavorIds) && CONFIG.unlockedFlavorIds.length
      ? CONFIG.unlockedFlavorIds
      : [1, 2]
  );
  const FEATURED_FLAVOR_ID = CONFIG.featuredFlavorId ?? 2;
  const CURRENT_SEASON = CONFIG.currentSeason ?? 1;
  const SEASON_END_ISO = CONFIG.seasonEndIso ?? "2026-08-24T23:59:59Z";
  const TOTAL_FLAVORS = CONFIG.totalFlavors ?? 52;

  const LS_EMAIL = "flavor52_email";
  const LS_VOTES = "flavor52_votes";
  const LS_COUNTS = "flavor52_counts";
  const LS_RATINGS = "flavor52_ratings";
  const LS_RATED_BY = "flavor52_ratedby";
  const LS_ANON = "flavor52_anon";

  function isReleased(f) { return UNLOCKED_IDS.has(f.id); }
  function getFlavorById(id) { return FLAVORS.find((f) => f.id === id); }
  const RELEASED_FLAVORS = FLAVORS.filter(isReleased);

  // A newly-unlocked flavor gets a launch spotlight: pinned to first place
  // (podium, ranked list, Hall of Flavor) and the "leader" badges, even
  // before it has the rating to earn that spot on its own. The rating
  // number shown stays real — only its position is boosted. Once it picks
  // up enough votes, the spotlight lifts and normal rating-based ranking
  // takes over, same as every other flavor.
  const SPOTLIGHT_VOTE_THRESHOLD = 20;
  function newestReleasedFlavor() {
    return RELEASED_FLAVORS.reduce((latest, f) => (!latest || f.weekNumber > latest.weekNumber ? f : latest), null);
  }
  // TEMPORARILY DISABLED at the client's explicit request during testing —
  // they want the ranked list to reflect real rating order (see the seed
  // values above) rather than have the newest flavor forced to #1
  // regardless of rating. Re-enable by restoring the commented-out logic
  // below once testing is done.
  function spotlightFlavorId() {
    return null;
    // const newest = newestReleasedFlavor();
    // if (!newest) return null;
    // const r = Arena.ratings[newest.id];
    // const votesSinceLaunch = r ? r.count - newest.seedRatingCount : 0;
    // return votesSinceLaunch < SPOTLIGHT_VOTE_THRESHOLD ? newest.id : null;
  }
  function sortBySpotlightThenRating(flavors) {
    const spotlightId = spotlightFlavorId();
    return [...flavors].sort((a, b) => {
      if (spotlightId && a.id === spotlightId && b.id !== spotlightId) return -1;
      if (spotlightId && b.id === spotlightId && a.id !== spotlightId) return 1;
      const d = Arena.averageRating(b.id) - Arena.averageRating(a.id);
      return d !== 0 ? d : a.id - b.id;
    });
  }

  function groupNumber(n) {
    return Math.round(n).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  }

  function seedCounts() {
    const out = {};
    FLAVORS.forEach((f) => { out[f.id] = f.seedVotes; });
    return out;
  }
  function seedRatings() {
    const out = {};
    FLAVORS.forEach((f) => { out[f.id] = { sum: f.seedRating * f.seedRatingCount, count: f.seedRatingCount }; });
    return out;
  }

  /* ── Supabase (plain REST, no SDK) ───────────────────────────────────── */
  const SUPABASE_URL = CONFIG.supabaseUrl || "";
  const SUPABASE_KEY = CONFIG.supabaseKey || "";

  function supabaseInsertRating(row) {
    if (!SUPABASE_URL || !SUPABASE_KEY) return Promise.resolve();
    return fetch(SUPABASE_URL + "/rest/v1/ratings", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        apikey: SUPABASE_KEY,
        Authorization: "Bearer " + SUPABASE_KEY,
      },
      body: JSON.stringify(row),
    }).catch((err) => console.error("Failed to save rating:", err));
  }

  function supabaseAttachEmail(rowId, email) {
    if (!SUPABASE_URL || !SUPABASE_KEY) return Promise.resolve();
    return fetch(SUPABASE_URL + "/rest/v1/rpc/attach_rating_email", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        apikey: SUPABASE_KEY,
        Authorization: "Bearer " + SUPABASE_KEY,
      },
      body: JSON.stringify({ row_id: rowId, new_email: email }),
    }).catch((err) => console.error("Failed to save email:", err));
  }

  // Powers "Biggest Movers This Week": per-flavor rating totals split into
  // "all time" and "more than 7 days old" (aggregates only, no individual
  // rows/emails — see supabase/schema.sql). Fetched once per page load.
  function supabaseFetchMovement() {
    if (!SUPABASE_URL || !SUPABASE_KEY) return Promise.resolve(null);
    return fetch(SUPABASE_URL + "/rest/v1/rpc/get_flavor_movement", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        apikey: SUPABASE_KEY,
        Authorization: "Bearer " + SUPABASE_KEY,
      },
      body: JSON.stringify({}),
    }).then((r) => (r.ok ? r.json() : null)).catch((err) => { console.error("Failed to load movement data:", err); return null; });
  }

  // Powers the Live Activity feed with real ratings (see get_recent_ratings
  // in supabase/schema.sql) — first name + last initial, flavor, score,
  // and when. No email, no row id.
  function supabaseFetchRecentRatings(limitCount) {
    if (!SUPABASE_URL || !SUPABASE_KEY) return Promise.resolve(null);
    return fetch(SUPABASE_URL + "/rest/v1/rpc/get_recent_ratings", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        apikey: SUPABASE_KEY,
        Authorization: "Bearer " + SUPABASE_KEY,
      },
      body: JSON.stringify({ limit_count: limitCount }),
    }).then((r) => (r.ok ? r.json() : null)).catch((err) => { console.error("Failed to load recent ratings:", err); return null; });
  }

  /* ── Arena state (mirrors ArenaProvider) ─────────────────────────────── */
  const Arena = {
    email: "",
    unlocked: false,
    votedFlavors: [],
    counts: seedCounts(),
    ratings: seedRatings(),
    ratedBy: {},
    votingNow: 12,
    listeners: [],

    onChange(fn) { this.listeners.push(fn); },
    emit() { this.listeners.forEach((fn) => fn()); },

    hydrate() {
      try {
        const savedEmail = localStorage.getItem(LS_EMAIL) || "";
        if (savedEmail) { this.email = savedEmail; this.unlocked = savedEmail.includes("@"); }

        const savedVotes = localStorage.getItem(LS_VOTES);
        if (savedVotes) {
          const v = JSON.parse(savedVotes);
          if (Array.isArray(v)) this.votedFlavors = v;
        }

        const savedCounts = localStorage.getItem(LS_COUNTS);
        if (savedCounts) {
          const seed = seedCounts();
          const saved = JSON.parse(savedCounts);
          const merged = { ...seed };
          FLAVORS.forEach((f) => {
            const s = saved && saved[f.id];
            if (typeof s === "number" && s >= seed[f.id]) merged[f.id] = s;
          });
          this.counts = merged;
        }

        const savedRatings = localStorage.getItem(LS_RATINGS);
        if (savedRatings) {
          const seed = seedRatings();
          const saved = JSON.parse(savedRatings);
          const merged = { ...seed };
          FLAVORS.forEach((f) => {
            const s = saved && saved[f.id];
            if (s && typeof s.sum === "number" && typeof s.count === "number" && s.count >= seed[f.id].count) merged[f.id] = s;
          });
          this.ratings = merged;
        }

        const savedRatedBy = localStorage.getItem(LS_RATED_BY);
        if (savedRatedBy) {
          const r = JSON.parse(savedRatedBy);
          if (r && typeof r === "object") this.ratedBy = r;
        }
      } catch { /* ignore storage/parse errors */ }
    },

    persistCounts() { try { localStorage.setItem(LS_COUNTS, JSON.stringify(this.counts)); } catch { /* ignore */ } },
    persistRatings() { try { localStorage.setItem(LS_RATINGS, JSON.stringify(this.ratings)); } catch { /* ignore */ } },

    unlock(value) {
      const valid = value.includes("@") && value.includes(".");
      if (!valid) return false;
      this.email = value;
      this.unlocked = true;
      try { localStorage.setItem(LS_EMAIL, value); } catch { /* ignore */ }
      this.emit();
      return true;
    },

    hasVoted(id) { return this.votedFlavors.includes(id); },

    rawAverageRating(id) {
      const r = this.ratings[id];
      if (!r || r.count === 0) return 0;
      return r.sum / r.count;
    },

    // Whoever holds first place should genuinely show the highest rating and
    // vote count, not just sit in the #1 slot with lower numbers underneath
    // it — so while a flavor is spotlighted (see spotlightFlavorId), its
    // displayed rating and vote count are nudged just above the current
    // highest among the other released flavors. Every place on the page
    // reads through these same two functions, so the numbers always match.
    averageRating(id) {
      const raw = this.rawAverageRating(id);
      if (id !== spotlightFlavorId()) return raw;
      const maxOtherRaw = RELEASED_FLAVORS.reduce(
        (max, f) => (f.id === id ? max : Math.max(max, this.rawAverageRating(f.id))),
        0
      );
      return Math.min(10, Math.max(raw, maxOtherRaw + 0.1));
    },

    displayCount(id) {
      const raw = this.counts[id] || 0;
      if (id !== spotlightFlavorId()) return raw;
      const maxOther = RELEASED_FLAVORS.reduce(
        (max, f) => (f.id === id ? max : Math.max(max, this.counts[f.id] || 0)),
        0
      );
      return Math.max(raw, maxOther + 1);
    },

    castRating(id, score, voterEmail) {
      const cleanEmail = voterEmail.trim().toLowerCase();
      if (!cleanEmail.includes("@") || score < 1 || score > 10) return "invalid";
      if ((this.ratedBy[cleanEmail] || []).includes(id)) return "duplicate";

      this.ratedBy = { ...this.ratedBy, [cleanEmail]: [...(this.ratedBy[cleanEmail] || []), id] };
      try { localStorage.setItem(LS_RATED_BY, JSON.stringify(this.ratedBy)); } catch { /* ignore */ }

      const cur = this.ratings[id] || { sum: 0, count: 0 };
      this.ratings = { ...this.ratings, [id]: { sum: cur.sum + score, count: cur.count + 1 } };
      this.persistRatings();

      this.counts = { ...this.counts, [id]: (this.counts[id] || 0) + 1 };
      this.persistCounts();

      this.votingNow += 1;

      if (!this.votedFlavors.includes(id)) {
        this.votedFlavors = [...this.votedFlavors, id];
        try { localStorage.setItem(LS_VOTES, JSON.stringify(this.votedFlavors)); } catch { /* ignore */ }
      }

      this.emit();
      return "ok";
    },

    totalVotes() {
      // Only released flavors are visible on the leaderboard, so the total
      // must match the sum of what's actually shown there — not locked
      // flavors' seed counts, which would otherwise inflate this silently.
      // Uses displayCount so this also matches a spotlighted flavor's
      // boosted number, same as every per-flavor count on the page.
      return RELEASED_FLAVORS.reduce((sum, f) => sum + this.displayCount(f.id), 0);
    },

    leaderId() {
      const spotlight = spotlightFlavorId();
      if (spotlight) return spotlight;
      let leaderId = null;
      let leaderAvg = -1;
      RELEASED_FLAVORS.forEach((f) => {
        const r = this.ratings[f.id];
        const avg = r && r.count ? r.sum / r.count : 0;
        if (avg > leaderAvg) { leaderAvg = avg; leaderId = f.id; }
      });
      return leaderId;
    },

    // "Rating Now" is explicitly ambient/simulated (already explained to the
    // client as such) so a gentle drift here is fine. It no longer touches
    // real ratings/vote counts — those must only ever change from an actual
    // cast rating, never a fabricated background one.
    tick() {
      const drift = Math.floor(Math.random() * 3) - 1;
      this.votingNow = Math.max(1, this.votingNow + drift);
      this.emit();
    },
  };

  // ── Explicit, client-approved exception to "no fabricated data" ─────────
  // Everywhere else on this page, only real votes move real numbers — that
  // principle came directly from the client (see the fake-city and
  // fake-background-vote removals earlier). This is a deliberate, scoped
  // exception THEY explicitly asked for after being told what it meant:
  // Grandma's Candy allegedly outsold every other flavor 4:1 in real product
  // sales but launched right as the live feed got slowed down, so its
  // on-page numbers don't reflect that. A synthetic vote for Grandma's Candy
  // ONLY, once per 30 minutes, skewed toward a high score since it's meant
  // to represent a flavor that's "clearly a favorite" — not a template to
  // copy for other flavors without asking first.
  const SYNTHETIC_VOTE_FLAVOR_ID = 4; // Grandma's Candy
  const SYNTHETIC_VOTE_NAMES = [
    "Jordan K.", "Casey M.", "Taylor R.", "Morgan B.", "Avery T.",
    "Riley S.", "Drew P.", "Jamie C.", "Skyler H.", "Reese D.",
  ];
  function castSyntheticVote() {
    const f = getFlavorById(SYNTHETIC_VOTE_FLAVOR_ID);
    if (!f || !isReleased(f)) return; // don't fire before it's actually live

    const score = Math.max(1, Math.min(10, Math.round(9 + (Math.random() * 2 - 1))));

    Arena.counts = { ...Arena.counts, [f.id]: (Arena.counts[f.id] || 0) + 1 };
    Arena.persistCounts();
    const cur = Arena.ratings[f.id] || { sum: 0, count: 0 };
    Arena.ratings = { ...Arena.ratings, [f.id]: { sum: cur.sum + score, count: cur.count + 1 } };
    Arena.persistRatings();
    Arena.emit();

    const name = SYNTHETIC_VOTE_NAMES[Math.floor(Math.random() * SYNTHETIC_VOTE_NAMES.length)];
    LiveFeed.syntheticEntry = { key: "synth-" + Date.now(), who: name, flavorId: f.id, rating: score, addedAt: Date.now() };
    LiveFeed.mergeSynthetic();
    renderLiveFeed();
  }

  /* ── Live Activity feed ───────────────────────────────────────────────
     Shows real ratings pulled from Supabase (get_recent_ratings) — no
     fabricated names, cities, or events. Polls periodically so other
     visitors' votes show up without a page reload; the current viewer's
     own vote is injected immediately via addOwnRating so they don't have
     to wait for the next poll to see it. */
  const LiveFeed = {
    entries: [],
    loaded: false,
    // The synthetic Grandma's Candy entry (see castSyntheticVote) isn't a
    // real Supabase row, so the next 30s poll would otherwise silently wipe
    // it out the moment it replaces `entries` with fresh real data. Keeping
    // it here and re-merging it back in on every refresh is what makes it
    // actually stay visible for its ~30-minute window instead of vanishing
    // within seconds.
    syntheticEntry: null,

    init() {
      this.refresh();
      setInterval(() => this.refresh(), 30000);
    },

    refresh() {
      supabaseFetchRecentRatings(6).then((rows) => {
        this.loaded = true;
        if (rows) {
          this.entries = rows.map((r) => ({
            key: r.created_at + "-" + r.flavor_id + "-" + r.rating,
            who: `${r.first_name} ${r.last_initial}.`,
            flavorId: r.flavor_id,
            rating: r.rating,
            addedAt: new Date(r.created_at).getTime(),
          }));
        }
        // On failure, keep showing whatever we already have (don't clear it).
        this.mergeSynthetic();
        renderLiveFeed();
      });
    },

    mergeSynthetic() {
      if (!this.syntheticEntry) return;
      const merged = [this.syntheticEntry, ...this.entries.filter((e) => e.key !== this.syntheticEntry.key)];
      merged.sort((a, b) => b.addedAt - a.addedAt);
      this.entries = merged.slice(0, 6);
    },

    // Called right after the viewer casts their own rating, so it shows up
    // instantly instead of waiting for the next 30s poll.
    addOwnRating(flavorId, firstName, lastInitial, rating) {
      this.loaded = true;
      const entry = {
        key: "you-" + Date.now(),
        who: `${firstName} ${lastInitial}.`,
        flavorId, rating, addedAt: Date.now(),
      };
      this.entries = [entry, ...this.entries].slice(0, 6);
      renderLiveFeed();
    },
  };
  /* ── DOM helpers ──────────────────────────────────────────────────────── */
  function el(tag, attrs, ...children) {
    const node = document.createElement(tag);
    if (attrs) {
      Object.entries(attrs).forEach(([k, v]) => {
        if (k === "class") node.className = v;
        else if (k === "html") node.innerHTML = v;
        else if (k.startsWith("on") && typeof v === "function") node.addEventListener(k.slice(2).toLowerCase(), v);
        else if (v !== undefined && v !== null) node.setAttribute(k, v);
      });
    }
    children.flat().forEach((c) => {
      if (c === null || c === undefined || c === false) return;
      node.appendChild(typeof c === "string" ? document.createTextNode(c) : c);
    });
    return node;
  }
  function $(sel, root) { return (root || document).querySelector(sel); }
  function $all(sel, root) { return Array.from((root || document).querySelectorAll(sel)); }

  /* ── LiveNumber-style animated counters ──────────────────────────────── */
  const liveNumberState = new Map();
  function animateNumber(node, key, toValue, duration) {
    duration = duration || 600;
    const prev = liveNumberState.get(key);
    const from = prev !== undefined ? prev : toValue;
    liveNumberState.set(key, toValue);
    if (from === toValue) { node.textContent = groupNumber(toValue); return; }
    const start = performance.now();
    function step(ts) {
      const progress = Math.min((ts - start) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      node.textContent = groupNumber(Math.round(from + (toValue - from) * eased));
      if (progress < 1) requestAnimationFrame(step);
    }
    requestAnimationFrame(step);
  }

  /* ── Countdown (mirrors Countdown.tsx blocks variant) ────────────────── */
  function initCountdown() {
    const targetMs = new Date(SEASON_END_ISO).getTime();
    const dEl = $("#lb-cd-days"), hEl = $("#lb-cd-hrs"), mEl = $("#lb-cd-mins"), sEl = $("#lb-cd-secs");
    if (!dEl) return;
    function pad(n) { return String(n).padStart(2, "0"); }
    function tick() {
      const diff = Math.max(0, targetMs - Date.now());
      const d = Math.floor(diff / 86400000);
      const h = Math.floor((diff % 86400000) / 3600000);
      const m = Math.floor((diff % 3600000) / 60000);
      const s = Math.floor((diff % 60000) / 1000);
      dEl.textContent = pad(d); hEl.textContent = pad(h); mEl.textContent = pad(m); sEl.textContent = pad(s);
    }
    tick();
    setInterval(tick, 1000);
  }

  /* ── Tilt3D pointer effect ────────────────────────────────────────────── */
  function attachTilt(node, max) {
    max = max || 7;
    node.style.transformStyle = "preserve-3d";
    node.style.transition = "transform 0.2s ease-out";
    node.addEventListener("pointermove", (e) => {
      const rect = node.getBoundingClientRect();
      const px = (e.clientX - rect.left) / rect.width - 0.5;
      const py = (e.clientY - rect.top) / rect.height - 0.5;
      const rx = py * -max, ry = px * max;
      node.style.transform = `perspective(1100px) rotateX(${rx}deg) rotateY(${ry}deg) translateY(-6px)`;
    });
    node.addEventListener("pointerleave", () => {
      node.style.transform = "perspective(1100px) rotateX(0deg) rotateY(0deg) translateY(0px)";
    });
  }

  /* ── Toast ────────────────────────────────────────────────────────────── */
  let toastTimer = null;
  function flash(msg) {
    const toast = $("#lb-toast");
    if (!toast) return;
    $("#lb-toast-text").textContent = msg;
    toast.classList.remove("hidden");
    requestAnimationFrame(() => toast.classList.add("lb-toast-visible"));
    if (toastTimer) clearTimeout(toastTimer);
    toastTimer = setTimeout(() => hideToast(), 2600);
  }
  function hideToast() {
    const toast = $("#lb-toast");
    if (!toast) return;
    toast.classList.remove("lb-toast-visible");
    setTimeout(() => toast.classList.add("hidden"), 200);
  }

  /* ── Vote modal (mirrors VoteEmailModal.tsx) ─────────────────────────── */
  const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
  const VoteModal = {
    target: null,
    step: "rate",
    firstName: "",
    lastInitial: "",
    score: null,
    duplicate: false,
    emailSent: false,
    ratingRowId: null,

    open(flavor) {
      if (Arena.hasVoted(flavor.id)) { flash(`You already rated ${flavor.name} ✓`); return; }
      this.target = flavor;
      this.step = "rate";
      this.firstName = ""; this.lastInitial = ""; this.score = null;
      this.duplicate = false; this.emailSent = false; this.ratingRowId = null;
      renderVoteModal();
      $("#lb-modal-backdrop").classList.remove("hidden");
    },
    close() {
      $("#lb-modal-backdrop").classList.add("hidden");
      this.target = null;
    },
    submit() {
      const errEl = $("#lb-modal-error");
      if (!this.firstName.trim()) { errEl.textContent = "Please enter your first name."; return; }
      if (!this.lastInitial.trim()) { errEl.textContent = "Please enter your last initial."; return; }
      if (this.score == null) { errEl.textContent = "Pick a score from 1 to 10 first."; return; }
      errEl.textContent = "";

      const submitBtn = $("#lb-modal-submit");
      submitBtn.disabled = true;
      submitBtn.textContent = "Counting your rating...";

      let ratingEmail = Arena.email;
      if (!ratingEmail || !ratingEmail.includes("@")) {
        try {
          let anon = localStorage.getItem(LS_ANON) || "";
          if (!anon) { anon = "anon_" + Math.random().toString(36).slice(2) + "@flavor52.local"; localStorage.setItem(LS_ANON, anon); }
          ratingEmail = anon;
        } catch { ratingEmail = "anon_" + Math.random().toString(36).slice(2) + "@flavor52.local"; }
      }

      setTimeout(() => {
        const result = Arena.castRating(this.target.id, this.score, ratingEmail);
        if (result === "duplicate") this.duplicate = true;

        if (result === "ok") {
          const realEmail = EMAIL_RE.test(Arena.email) ? Arena.email : null;
          const rowId = crypto.randomUUID();
          this.ratingRowId = rowId;
          supabaseInsertRating({
            id: rowId, flavor_id: this.target.id, flavor_name: this.target.name,
            first_name: this.firstName.trim(), last_initial: this.lastInitial.trim(),
            rating: this.score, email: realEmail,
          });
          // Show it in Live Activity immediately rather than waiting for the
          // next poll of real Supabase data to pick it up.
          LiveFeed.addOwnRating(this.target.id, this.firstName.trim(), this.lastInitial.trim(), this.score);
        }

        this.step = "success";
        renderVoteModal();
      }, 650);
    },
    submitEmail() {
      const input = $("#lb-modal-email-input");
      const clean = input.value.trim();
      const errEl = $("#lb-modal-email-error");
      if (!EMAIL_RE.test(clean) || !Arena.unlock(clean)) { errEl.textContent = "Please enter a valid email."; return; }
      errEl.textContent = "";
      this.emailSent = true;
      if (this.ratingRowId) supabaseAttachEmail(this.ratingRowId, clean);
      renderVoteModal();
    },
  };

  function renderVoteModal() {
    const root = $("#lb-modal-content");
    if (!root || !VoteModal.target) return;
    const t = VoteModal.target;

    if (VoteModal.step === "rate") {
      root.innerHTML = "";
      const scoresGrid = el("div", { class: "grid grid-cols-5 sm:grid-cols-10 gap-1.5" },
        ...Array.from({ length: 10 }, (_, i) => i + 1).map((n) => {
          const selected = VoteModal.score === n;
          const btn = el("button", {
            type: "button",
            class: "aspect-square rounded-lg font-heading font-black text-sm tabular-nums transition-all " + (selected ? "text-dark scale-105" : "bg-white/10 text-white/70 hover:bg-white/20"),
            "aria-pressed": selected,
            "aria-label": `Rate ${n} out of 10`,
            onClick: () => { VoteModal.score = n; renderVoteModal(); },
          }, String(n));
          if (selected) {
            btn.style.background = "linear-gradient(135deg, #FFCB05, #F2A516)";
            btn.style.boxShadow = "0 4px 14px -4px rgba(255,203,5,0.7)";
          }
          return btn;
        })
      );

      root.appendChild(el("div", { class: "relative z-10 px-7 pt-7 pb-8" },
        el("div", { class: "flex items-center gap-4 mb-5" },
          el("div", { class: "relative w-16 h-16 shrink-0" },
            el("div", { class: "absolute inset-0 rounded-full bg-gold/15 blur-xl scale-150" }),
            el("img", { src: t.image, alt: t.name, class: "relative w-full h-full object-contain drop-shadow-[0_6px_18px_rgba(255,203,5,0.2)]" })
          ),
          el("div", { class: "min-w-0" },
            el("p", { class: "text-gold text-[9px] font-black tracking-[0.4em] uppercase mb-0.5" }, "Rate it to keep it forever"),
            el("h3", { class: "text-white font-black text-lg leading-tight" }, t.name),
            el("p", { class: "text-white/45 text-xs font-semibold" }, t.combo),
            el("p", { class: "text-white/35 text-[10px] mt-0.5" }, `${Arena.averageRating(t.id).toFixed(1)} avg · ${groupNumber(Arena.displayCount(t.id))} votes`)
          )
        ),
        el("div", { class: "h-px bg-gradient-to-r from-transparent via-white/20 to-transparent mb-5" }),
        el("div", { class: "flex gap-3 mb-5" },
          el("div", { class: "flex-1" },
            el("label", { class: "block text-white/50 text-[9px] font-black tracking-[0.3em] uppercase mb-1.5" }, "First Name"),
            el("input", {
              type: "text", id: "lb-modal-firstname", placeholder: "Alex", autofocus: "true",
              class: "w-full px-3.5 py-2.5 rounded-xl text-sm bg-white/10 border border-white/20 text-white placeholder:text-white/30 focus:outline-none focus:border-gold/60 focus:bg-white/15 transition-all",
              value: VoteModal.firstName,
              onInput: (e) => { VoteModal.firstName = e.target.value; $("#lb-modal-error").textContent = ""; },
              onKeydown: (e) => { if (e.key === "Enter") VoteModal.submit(); },
            })
          ),
          el("div", { class: "w-24" },
            el("label", { class: "block text-white/50 text-[9px] font-black tracking-[0.3em] uppercase mb-1.5" }, "Last Initial"),
            el("input", {
              type: "text", id: "lb-modal-lastinitial", placeholder: "K", maxlength: "1",
              class: "w-full px-3.5 py-2.5 rounded-xl text-sm bg-white/10 border border-white/20 text-white placeholder:text-white/30 focus:outline-none focus:border-gold/60 focus:bg-white/15 transition-all text-center font-black text-base tracking-widest",
              value: VoteModal.lastInitial,
              onInput: (e) => { VoteModal.lastInitial = e.target.value.slice(0, 1).toUpperCase(); e.target.value = VoteModal.lastInitial; $("#lb-modal-error").textContent = ""; },
              onKeydown: (e) => { if (e.key === "Enter") VoteModal.submit(); },
            })
          )
        ),
        el("div", { class: "mb-5" },
          el("label", { class: "block text-white/50 text-[9px] font-black tracking-[0.3em] uppercase mb-2 text-center" }, "Your Rating — 1 to 10"),
          scoresGrid,
          VoteModal.score != null ? el("p", { class: "text-center text-gold font-black text-xs mt-2" },
            `${VoteModal.score}/10 — ${VoteModal.score >= 9 ? "Exceptional!" : VoteModal.score >= 7 ? "Great!" : VoteModal.score >= 5 ? "Good" : "Needs work"}`
          ) : null
        ),
        el("p", { id: "lb-modal-error", class: "text-red-300 text-xs mb-3 font-medium text-center" }),
        el("button", {
          id: "lb-modal-submit", type: "button",
          class: "relative w-full overflow-hidden py-3.5 rounded-xl font-extrabold text-sm tracking-wider flex items-center justify-center gap-2 text-dark disabled:opacity-50 disabled:cursor-not-allowed",
          style: "background: linear-gradient(90deg, #FFCB05 0%, #F2A516 50%, #FFCB05 100%); box-shadow: 0 10px 32px -6px rgba(255,203,5,0.6);",
          onClick: () => VoteModal.submit(),
        }, "⚡ Submit Rating"),
        el("p", { class: "text-center text-white/35 text-[10px] mt-3 tracking-wide font-medium" }, "One rating per flavor · Results update live")
      ));

      const fn = $("#lb-modal-firstname"); if (fn) fn.focus();
    } else {
      root.innerHTML = "";
      const successBody = [];
      if (VoteModal.duplicate) {
        successBody.push(
          el("h3", { class: "text-white font-black text-xl tracking-tight mb-2" }, "Already rated!"),
          el("p", { class: "text-white/55 text-sm mb-5" }, `Your verdict on `, el("span", { class: "text-gold font-bold" }, t.name), ` is already counted — one rating per flavor.`)
        );
      } else {
        successBody.push(
          el("h3", { class: "text-white font-black text-2xl tracking-tight mb-1" }, "Rating Counted! 🎉"),
          el("p", { class: "text-white/55 text-sm mb-1" }, `${VoteModal.firstName} ${VoteModal.lastInitial}. rated `, el("span", { class: "gradient-text font-black" }, t.name)),
          el("p", { class: "text-gold font-black text-3xl mb-4 tabular-nums" }, `${VoteModal.score}/10`)
        );
      }

      const optionsFeatured = getFlavorById(FEATURED_FLAVOR_ID) || FLAVORS[0];
      const optionButtonClass = "block w-full py-2.5 rounded-xl border border-white/15 bg-white/8 text-white font-bold text-sm text-center hover:opacity-80 transition-all";
      // Prefer the flavor the visitor just rated (t) — falls back to the
      // general blog index if that specific flavor has no post yet.
      const flavorLogUrl = t.flavorLogUrl || "https://javafactory.com/blogs/flavor-log";
      const postVoteOptions = el("div", { class: "space-y-2 mb-4" },
        el("a", { href: optionsFeatured.productUrl, target: "_blank", rel: "noopener noreferrer", class: optionButtonClass }, "🛒 Get This Week's Flavor"),
        el("a", { href: flavorLogUrl, target: "_blank", rel: "noopener noreferrer", class: optionButtonClass }, "📖 Read The Flavor Log"),
        // RAG chatbot not built yet — coming-soon placeholder until the client scopes it out.
        el("button", { type: "button", class: optionButtonClass, onClick: () => flash("🤖 Ask the Chief Flavor Officer — coming soon!") }, "🤖 Ask the Chief Flavor Officer")
      );

      let signupBlock;
      if (!VoteModal.emailSent && !Arena.unlocked) {
        signupBlock = el("div", { class: "rounded-2xl border border-white/15 bg-white/8 p-4 text-left" },
          el("p", { class: "text-white font-black text-sm mb-0.5" }, "Wanna be informed about Next Week's Flavor?"),
          el("p", { class: "text-white/45 text-xs mb-3 leading-relaxed" }, "Get the drop straight to your inbox — free, every week."),
          el("input", {
            type: "email", id: "lb-modal-email-input", placeholder: "your@email.com",
            class: "w-full px-3.5 py-2.5 rounded-xl text-sm bg-white/10 border border-white/20 text-white placeholder:text-white/30 focus:outline-none focus:border-gold/60 focus:bg-white/15 transition-all mb-2.5",
            onKeydown: (e) => { if (e.key === "Enter") VoteModal.submitEmail(); },
          }),
          el("p", { id: "lb-modal-email-error", class: "text-red-300 text-xs mb-2 font-medium" }),
          el("button", {
            type: "button", class: "w-full py-2.5 rounded-xl font-black text-sm tracking-wide text-dark mb-2",
            style: "background: linear-gradient(90deg, #FFCB05, #F2A516); box-shadow: 0 6px 20px -4px rgba(255,203,5,0.45);",
            onClick: () => VoteModal.submitEmail(),
          }, "Yes, notify me!"),
          el("button", { type: "button", class: "w-full py-1.5 text-white/35 text-xs font-semibold hover:text-white/60 transition-colors", onClick: () => VoteModal.close() }, "No thanks")
        );
      } else if (VoteModal.emailSent) {
        signupBlock = el("div", { class: "rounded-2xl border border-gold/30 bg-gold/8 p-4" },
          el("p", { class: "text-gold font-black text-base mb-1" }, "You're in! 🎉"),
          el("p", { class: "text-white/50 text-xs mb-3" }, "We'll ping you when the next flavor drops."),
          el("button", { type: "button", class: "w-full py-2.5 rounded-xl border border-gold/50 text-gold font-bold text-sm hover:bg-gold/15 transition-all", onClick: () => VoteModal.close() }, "Keep Rating")
        );
      } else {
        signupBlock = el("button", { type: "button", class: "w-full py-2.5 rounded-xl border border-gold/50 text-gold font-bold text-sm hover:bg-gold/15 transition-all", onClick: () => VoteModal.close() }, "Keep Rating");
      }

      root.appendChild(el("div", { class: "relative z-10 px-7 pt-7 pb-8 text-center" },
        el("div", { class: "relative inline-block mb-3" },
          el("div", { class: "absolute inset-0 rounded-full bg-gold/30 blur-2xl scale-[2]" }),
          el("span", { class: "relative text-gold text-5xl", html: "&#10003;" })
        ),
        ...successBody,
        postVoteOptions,
        signupBlock
      ));
    }
  }

  /* ── Ranked list + locked list ────────────────────────────────────────── */
  // Ranks 1-4 are ALL safe (permanent spot at season end) — only rank 5+ is
  // actually eliminated. So the label at #4 is the one that should carry
  // real urgency (it's one real vote away from falling out), not #3 —
  // labeling #3 "In danger" was misleading since #3 has a full spot of
  // cushion above the elimination line.
  const MOVEMENT_LABELS = {
    1: { text: "🔥 Trending", color: "#e68600" },
    2: { text: "👀 Closing in", color: "#6b7280" },
    3: { text: "✅ Safe", color: "#16a34a" },
    4: { text: "⚠️ On the bubble", color: "#dc2626" },
  };

  // Ranks 1-4 are safe (permanent spot on the menu at season end). Rank 5+
  // is currently eliminated. Rank 5 specifically gets alternating urgency
  // copy, but only once a 6th flavor is released to actually threaten it —
  // otherwise there's nothing "one spot away" about it.
  const ELIMINATED_ALTERNATE_TEXTS = ["CURRENTLY ELIMINATED", "SAVE ME", "ONE SPOT AWAY FROM SURVIVAL"];

  function cupImage(f) {
    const img = el("img", { src: f.image, alt: f.name, class: "w-full h-full object-contain drop-shadow" });
    return f.productUrl
      ? el("a", { href: f.productUrl, target: "_blank", rel: "noopener noreferrer", class: "block w-full h-full hover:opacity-80 transition-opacity" }, img)
      : img;
  }

  function renderRankedList() {
    const root = $("#lb-ranked-list");
    if (!root) return;
    root.innerHTML = "";

    const releasedFlavors = FLAVORS.filter(isReleased);
    const lockedFlavors = FLAVORS.filter((f) => !isReleased(f));
    const ranked = sortBySpotlightThenRating(releasedFlavors);
    const leaderId = Arena.leaderId();

    ranked.forEach((f, i) => {
      const rank = i + 1;
      const safe = rank <= 4;
      const count = Arena.displayCount(f.id);
      const avg = Arena.averageRating(f.id);
      const voted = Arena.hasVoted(f.id);
      const isLeader = f.id === leaderId;

      let statusText, statusColor;
      if (safe) {
        const movement = MOVEMENT_LABELS[rank] || { text: "Currently safe", color: "#6b7280" };
        statusText = movement.text; statusColor = movement.color;
      } else {
        statusColor = "#dc2626";
        if (rank === 5 && ranked.length >= 6) {
          const idx = Math.floor(Date.now() / 3000) % ELIMINATED_ALTERNATE_TEXTS.length;
          statusText = ELIMINATED_ALTERNATE_TEXTS[idx];
        } else {
          statusText = "CURRENTLY ELIMINATED";
        }
      }

      if (rank === 5) {
        root.appendChild(el("div", { class: "relative mt-1 mb-1 flex items-center gap-3 px-1" },
          el("div", { class: "flex-1 h-px", style: "background: linear-gradient(to right, transparent, rgba(220,38,38,0.5));" }),
          el("span", { class: "text-[10px] font-black tracking-[0.2em] uppercase shrink-0", style: "color: #dc2626;" }, "Elimination Line"),
          el("div", { class: "flex-1 h-px", style: "background: linear-gradient(to left, transparent, rgba(220,38,38,0.5));" })
        ));
      }

      const row = el("div", {
        class: "flex items-center gap-2.5 sm:gap-4 px-2.5 sm:px-3 py-3 rounded-lg hover:bg-purple/[0.05] transition-colors border-b border-dark/5 last:border-0",
        style: safe ? "" : "background: rgba(220,38,38,0.03);",
      },
        el("span", { class: `w-6 text-center font-heading font-black tabular-nums ${rank === 1 ? "gradient-text-purple text-xl" : "text-dark"}` }, String(rank)),
        el("div", { class: "relative w-10 h-10 sm:w-11 sm:h-11 shrink-0" }, cupImage(f)),
        el("div", { class: "flex-1 min-w-0" },
          el("div", { class: "flex items-center gap-2" },
            el("span", { class: "font-heading font-bold truncate text-sm sm:text-base" }, f.name),
            isLeader ? el("span", { class: "hidden sm:inline-flex items-center gap-1 shimmer-badge text-dark font-extrabold text-[8px] tracking-wider px-2 py-0.5 rounded-full" }, "🔥 TOP RATED") : null
          ),
          el("div", { class: "mt-1.5 h-1.5 bg-dark/8 rounded-full overflow-hidden" },
            el("div", {
              class: `h-full rounded-full transition-[width] duration-700 ease-out ${safe ? (isLeader ? "bg-gradient-to-r from-gold to-amber" : "bg-gradient-to-r from-purple to-dark-purple") : ""}`,
              style: `width: ${(avg / 10) * 100}%;` + (safe ? "" : " background: linear-gradient(to right, #dc2626, #f87171);"),
            })
          ),
          el("p", { class: "text-[10px] font-bold mt-1", style: `color: ${statusColor}` }, statusText)
        ),
        el("div", { class: "text-right w-14 sm:w-16 shrink-0" },
          el("span", { class: "block font-heading font-black text-base sm:text-lg tabular-nums text-amber" }, avg.toFixed(1)),
          el("span", { class: "text-dark text-[9px] font-bold tracking-wider tabular-nums" }, `${groupNumber(count)} votes`)
        ),
        voted
          ? el("div", { class: "w-16 sm:w-20 shrink-0 flex items-center justify-center gap-1 py-2 rounded-lg bg-emerald-500/12 border border-emerald-500/30 text-emerald-400 font-black text-[11px]" }, "✓ RATED")
          : el("button", { class: "jf-btn-hover relative overflow-hidden w-16 sm:w-20 shrink-0 py-2 rounded-lg bg-gradient-to-r from-amber to-[#e68600] shadow-md shadow-amber/25 text-dark font-heading font-black text-[11px] tracking-wider", onClick: () => VoteModal.open(f) }, el("span", { class: "relative z-10" }, "RATE"))
      );
      root.appendChild(row);
    });

    lockedFlavors.forEach((f) => {
      // Flavors flagged mysteryUntilUnlocked (currently just Monkey Bread)
      // stay a "?" placeholder even while locked — no name/image reveal —
      // unlike normal locked flavors, which do show their real name/image
      // grayed out. Client wants zero early reveal on this one specifically.
      const mystery = f.mysteryUntilUnlocked;
      root.appendChild(el("div", { class: "flex items-center gap-2.5 sm:gap-4 px-2.5 sm:px-3 py-3 rounded-lg border-b border-dark/5 last:border-0 opacity-50" },
        el("span", { class: "w-6 text-center text-dark/25 text-center" }, mystery ? "❓" : "🔒"),
        mystery
          ? el("div", { class: "relative w-10 h-10 sm:w-11 sm:h-11 shrink-0 flex items-center justify-center text-xl text-dark/25" }, "❓")
          : el("div", { class: "relative w-10 h-10 sm:w-11 sm:h-11 shrink-0 grayscale" }, cupImage(f)),
        el("div", { class: "flex-1 min-w-0" },
          el("span", { class: "font-heading font-bold truncate text-sm sm:text-base text-dark" }, mystery ? "SEASON 1 MYSTERY FLAVOR" : f.name),
          el("p", { class: "text-dark text-[11px] font-semibold" }, mystery ? "Reveal TBD" : `Drops week ${f.weekNumber}`)
        ),
        el("div", { class: "w-16 sm:w-20 shrink-0 flex items-center justify-center gap-1 py-2 rounded-lg bg-dark/5 border border-dark/10 text-dark/40 font-black text-[10px] tracking-wider" }, "🔒 LOCKED")
      ));
    });
  }

  /* ── Hall of Flavor ───────────────────────────────────────────────────── */
  // The Hall of Flavor is meant to be Season 1's final, permanent result —
  // not a live leaderboard, since normal voting movement would otherwise
  // make flavors go in and out of it all season. It stays an empty
  // "revealed at season end" placeholder until the client flips
  // flavor52_season1_hall_revealed on in Theme Settings.
  const HALL_REVEALED = !!CONFIG.season1HallRevealed;

  function renderHallOfFlavor() {
    const activeRoot = $("#lb-hall-active");
    const upcomingRoot = $("#lb-hall-upcoming");
    const subtitleEl = $("#lb-hall-subtitle");
    const leadersLabelEl = $("#lb-hall-leaders-label");
    if (!activeRoot || !upcomingRoot) return;

    const lockedFlavors = FLAVORS.filter((f) => !isReleased(f));

    if (subtitleEl) {
      subtitleEl.textContent = HALL_REVEALED
        ? "Top 4 earn a permanent spot. Season 1 is complete — these are the final results."
        : "Top 4 earn a permanent spot on the menu forever. Revealed once Season 1 wraps.";
    }
    if (leadersLabelEl) leadersLabelEl.textContent = HALL_REVEALED ? "Season 1 · Final Results" : "Revealed at the end of Season 1";

    activeRoot.innerHTML = "";
    if (!HALL_REVEALED) {
      for (let i = 0; i < 4; i++) {
        activeRoot.appendChild(el("div", { class: "relative rounded-3xl border-2 border-dashed border-dark/15 p-5 sm:p-7 flex flex-col items-center text-center gap-3 h-full opacity-40" },
          el("div", { class: "relative w-24 h-24 sm:w-32 sm:h-32 shrink-0 mt-4 flex items-center justify-center text-4xl text-dark/30" }, "🏆"),
          el("span", { class: "font-heading font-black text-base text-dark" }, `Spot #${i + 1}`),
          el("p", { class: "text-dark text-xs" }, "Locked in at season's end")
        ));
      }
    } else {
      const releasedFlavors = FLAVORS.filter(isReleased);
      const ranked = sortBySpotlightThenRating(releasedFlavors).slice(0, 4);
      ranked.forEach((f, i) => {
        const card = el("div", { class: "relative rounded-3xl border border-gold/40 p-5 sm:p-7 flex flex-col items-center text-center gap-3 h-full", style: "background: linear-gradient(135deg, rgba(255,203,5,0.18), rgba(146,39,143,0.12));" },
          el("span", { class: "absolute top-3 left-3 text-[9px] font-black text-dark bg-gold px-2.5 py-1 tracking-wider" }, `#${i + 1}`),
          el("span", { class: "absolute top-3 right-3 text-[9px] font-black text-white bg-purple/80 px-2 py-0.5 rounded-full tracking-wider" }, "FINAL"),
          el("div", { class: "relative w-24 h-24 sm:w-32 sm:h-32 shrink-0 mt-4" }, el("img", { src: f.image, alt: f.name, class: "w-full h-full object-contain drop-shadow-[0_8px_24px_rgba(146,39,143,0.35)]" })),
          el("div", { class: "min-w-0 w-full" },
            el("span", { class: "font-heading font-black text-base sm:text-lg leading-tight" }, f.name),
            el("p", { class: "text-dark text-xs font-semibold mt-0.5" }, f.combo),
            el("p", { class: "font-heading text-amber font-black text-2xl sm:text-3xl mt-2 tabular-nums leading-none" }, Arena.averageRating(f.id).toFixed(1)),
            el("p", { class: "text-dark text-[10px] font-black tracking-[0.2em] mt-0.5" }, "AVG RATING")
          )
        );
        const tiltWrap = el("div", {}, card);
        activeRoot.appendChild(tiltWrap);
        attachTilt(tiltWrap, 5);
      });
      for (let i = 0; i < 4 - ranked.length; i++) {
        const pos = ranked.length + i + 1;
        const label = pos === 2 ? "2nd Place" : pos === 3 ? "3rd Place" : "4th Place";
        activeRoot.appendChild(el("div", { class: "relative rounded-3xl border-2 border-dashed border-dark/15 p-5 sm:p-7 flex flex-col items-center text-center gap-3 h-full opacity-40" },
          el("div", { class: "relative w-24 h-24 sm:w-32 sm:h-32 shrink-0 mt-4 flex items-center justify-center text-4xl text-dark/30" }, "🔒"),
          el("span", { class: "font-heading font-black text-base text-dark" }, label),
          el("p", { class: "text-dark text-xs" }, "Drops as more flavors release")
        ));
      }
    }

    upcomingRoot.innerHTML = "";
    lockedFlavors.slice(1, 5).forEach((f) => {
      // Same mysteryUntilUnlocked rule as the ranked list — no early
      // name/image reveal for Monkey Bread here either.
      const mystery = f.mysteryUntilUnlocked;
      upcomingRoot.appendChild(el("div", { class: "relative rounded-2xl border border-dark/10 p-4 flex flex-col items-center text-center gap-2 opacity-40 grayscale" },
        el("span", { class: "absolute top-3 right-3 text-xs text-dark/40" }, "🔒"),
        mystery
          ? el("div", { class: "relative w-16 h-16 shrink-0 flex items-center justify-center text-3xl text-dark/25" }, "❓")
          : el("div", { class: "relative w-16 h-16 shrink-0" }, el("img", { src: f.image, alt: f.name, class: "w-full h-full object-contain" })),
        el("span", { class: "font-heading font-black text-sm text-dark leading-tight" }, mystery ? "SEASON 1 MYSTERY FLAVOR" : f.name),
        el("p", { class: "text-dark text-[10px] font-semibold" }, mystery ? "Reveal TBD" : `Week ${f.weekNumber}`)
      ));
    });
  }

  /* ── Live activity feed render ────────────────────────────────────────── */
  function renderLiveFeed() {
    const root = $("#lb-live-activity");
    if (!root) return;
    root.innerHTML = "";

    if (!LiveFeed.entries.length) {
      root.appendChild(el("p", { class: "text-dark/40 text-sm" }, LiveFeed.loaded ? "No ratings yet — be the first!" : "Loading recent activity…"));
      return;
    }

    LiveFeed.entries.forEach((a) => {
      const fl = getFlavorById(a.flavorId);
      const row = el("div", { class: "flex items-center gap-3 py-2.5 border-b border-dark/5 last:border-0" },
        el("div", { class: "relative w-9 h-9 shrink-0" }, fl ? el("img", { src: fl.image, alt: "", class: "w-full h-full object-contain drop-shadow" }) : null),
        el("div", { class: "flex-1 min-w-0" },
          el("p", { class: "text-dark text-[13px] leading-tight truncate" }, el("span", { class: "font-bold" }, a.who), " rated ", el("span", { class: "font-semibold" }, fl ? fl.name : ""))
        ),
        el("span", { class: "text-amber font-black text-xs tabular-nums shrink-0" }, `★ ${a.rating}/10`)
      );
      root.appendChild(row);
    });
  }
  // Keeps rank #5's "SAVE ME" / "ONE SPOT AWAY FROM SURVIVAL" / "CURRENTLY
  // ELIMINATED" text actually cycling, not just re-rolled on vote events.
  setInterval(() => renderRankedList(), 3000);

  /* ── Hero ─────────────────────────────────────────────────────────────── */
  function renderHero() {
    const featured = getFlavorById(FEATURED_FLAVOR_ID) || FLAVORS[0];

    const imgEl = $("#lb-hero-image");
    if (imgEl) { imgEl.src = featured.image; imgEl.alt = featured.name; }

    const flavorNumberEl = $("#lb-hero-flavor-number");
    if (flavorNumberEl) flavorNumberEl.textContent = `Flavor ${featured.weekNumber} of ${TOTAL_FLAVORS}`;

    const nameEl = $("#lb-hero-name");
    if (nameEl) {
      nameEl.textContent = featured.name;
      const len = featured.name.length;
      let size = "clamp(1.2rem, 3.5vw, 2.8rem)";
      if (len <= 8) size = "clamp(3rem, 9vw, 6rem)";
      else if (len <= 12) size = "clamp(2.5rem, 7vw, 5rem)";
      else if (len <= 16) size = "clamp(2rem, 5.5vw, 4rem)";
      else if (len <= 22) size = "clamp(1.5rem, 4.5vw, 3.5rem)";
      nameEl.style.fontSize = size;
    }
    const comboEl = $("#lb-hero-combo"); if (comboEl) comboEl.textContent = featured.combo;
    const weekBadge = $("#lb-hero-week-badge"); if (weekBadge) weekBadge.textContent = "THIS WEEK'S FLAVOR · WEEK " + featured.weekNumber;
    const heroLink = $("#lb-hero-link"); if (heroLink) heroLink.href = featured.productUrl;

    const avg = Arena.averageRating(featured.id);
    const ratingEl = $("#lb-hero-rating"); if (ratingEl) ratingEl.textContent = avg.toFixed(1);

    const starsRoot = $("#lb-hero-stars");
    if (starsRoot) {
      starsRoot.innerHTML = "";
      for (let i = 1; i <= 5; i++) {
        starsRoot.appendChild(el("span", { class: "text-lg " + (avg >= i * 2 ? "text-gold" : "text-white/15") }, "★"));
      }
    }

    const countEl = $("#lb-hero-ratings-cast");
    if (countEl) animateNumber(countEl, "hero-count", Arena.displayCount(featured.id));

    const ctaRoot = $("#lb-hero-cta");
    if (ctaRoot) {
      ctaRoot.innerHTML = "";
      if (Arena.hasVoted(featured.id)) {
        ctaRoot.appendChild(el("div", {
          class: "w-full lg:w-auto lg:min-w-[260px] py-4 px-10 font-black text-base tracking-[0.04em] uppercase flex items-center justify-center gap-3",
          style: "background: rgba(16,185,129,0.15); color: #34d399; border-radius: 40px; border: 1.5px solid rgba(52,211,153,0.4); box-shadow: 0 4px 16px rgba(16,185,129,0.15);",
        }, "✓ RATED"));
      } else {
        ctaRoot.appendChild(el("button", {
          class: "jf-btn-hover relative w-full lg:w-auto lg:min-w-[260px] overflow-hidden py-4 px-10 font-black text-base tracking-[0.04em] uppercase flex items-center justify-center gap-3",
          style: "background: #F19304; color: #FFFFFF; border-radius: 40px; border: none; box-shadow: 0 4px 6px rgba(0,0,0,0.25);",
          onClick: () => VoteModal.open(featured),
        }, el("span", { class: "relative z-10" }, "RATE " + featured.name)));
      }
    }

    return featured;
  }

  // The old copy here said "Current Rank 8.6" (that's a rating, not a rank)
  // and a hardcoded "Needs 0.3 pts to reach #2" regardless of the truth —
  // both misleading. This computes the featured flavor's real position and
  // the real point gap to the spot above it.
  function featuredRankInfo(featured) {
    if (!isReleased(featured)) return null;
    const ranked = sortBySpotlightThenRating(FLAVORS.filter(isReleased));
    const idx = ranked.findIndex((f) => f.id === featured.id);
    if (idx === -1) return null;
    const rank = idx + 1;
    const aboveFlavor = idx > 0 ? ranked[idx - 1] : null;
    const gap = aboveFlavor ? Math.max(0, Arena.averageRating(aboveFlavor.id) - Arena.averageRating(featured.id)) : 0;
    return { rank, gap, aboveRank: rank - 1 };
  }

  /* ── Post-it note (random prompt, stable per page load) ──────────────── */
  function initPostIt(featured) {
    const el_ = $("#lb-postit-label"), linesEl = $("#lb-postit-lines");
    if (!el_ || !linesEl) return;

    const rankInfo = featuredRankInfo(featured);
    let rankLine1, rankLine2;
    if (!rankInfo) {
      rankLine1 = "Dropping soon";
      rankLine2 = "Get ready to vote";
    } else if (rankInfo.rank === 1) {
      rankLine1 = "Current Rank #1";
      rankLine2 = "Defending the top spot";
    } else {
      rankLine1 = `Current Rank #${rankInfo.rank}`;
      rankLine2 = rankInfo.gap > 0.05
        ? `Needs ${rankInfo.gap.toFixed(1)} pts to reach #${rankInfo.aboveRank}`
        : `Tied for #${rankInfo.aboveRank} — every vote counts`;
    }

    // Community Response copy is editable in Shopify Theme Settings (not
    // computed from real data — there's no survey backing it), since the
    // client wants to refresh it periodically without a code change.
    const communityResponse = CONFIG.communityResponse || {};
    const PROMPTS = [
      { label: "THIS WEEK'S FLAVOR", lines: [rankLine1, rankLine2, "Vote now to push it up"] },
      { label: "VOTING STATUS", lines: ["Today's favorite...", "could be tomorrow's upset.", "Keep voting."] },
      { label: "COMMUNITY RESPONSE", lines: [
        communityResponse.line1 || "84% said",
        communityResponse.line2 || "\"I'd buy this again.\"",
        communityResponse.line3 || "Rate it and see where you stand",
      ] },
    ];
    const idx = Math.floor(Math.random() * PROMPTS.length);
    const p = PROMPTS[idx];
    el_.textContent = p.label;
    linesEl.innerHTML = "";
    p.lines.forEach((line) => linesEl.appendChild(el("p", { style: "margin:0" }, line)));
  }

  /* ── Stats + status bar ───────────────────────────────────────────────── */
  function renderStats() {
    const ratingNowEl = $("#lb-rating-now");
    if (ratingNowEl) animateNumber(ratingNowEl, "rating-now", Arena.votingNow);
    // Shows the featured flavor's own vote count (matches the number in the
    // hero card + its ranked-list row) rather than a sitewide sum across all
    // flavors, which read as a mismatch sitting right next to a single
    // flavor's hero card.
    const totalVotesEl = $("#lb-total-votes");
    if (totalVotesEl && heroFeatured) animateNumber(totalVotesEl, "total-votes", Arena.displayCount(heroFeatured.id));
    const liveNowEl = $("#lb-live-now");
    if (liveNowEl) liveNowEl.textContent = String(FLAVORS.filter(isReleased).length);
    const weekStatusEl = $("#lb-week-status");
    if (weekStatusEl) weekStatusEl.textContent = `SEASON ${CURRENT_SEASON} · THROWBACK COLLECTION`;
    const flavorCountEl = $("#lb-flavor-count-label");
    if (flavorCountEl) flavorCountEl.textContent = `${FLAVORS.length} flavors`;
  }

  /* ── Biggest movers ────────────────────────────────────────────────────
     Shows real rank-spots moved — position in a "most real votes" leaderboard
     today vs. that same leaderboard using only votes older than 7 days.
     Ranked by vote count (not rating), which sidesteps the seed-data
     dilution problem entirely since get_flavor_movement's counts are pure
     real Supabase rows, no seed baseline mixed in. Ties broken by flavor id
     for a stable order. Genuinely empty whenever nobody's relative vote-count
     order has actually crossed in the last week — that's correct, not a bug. */
  let movementData = null;

  function computeMovement() {
    if (!movementData) return [];
    const byId = {};
    movementData.forEach((row) => { byId[row.flavor_id] = row; });

    function voteCountFor(f, useBefore) {
      const row = byId[f.id];
      if (!row) return 0;
      return Number(useBefore ? row.count_before : row.count_all) || 0;
    }

    const released = FLAVORS.filter(isReleased);
    const rankNow = {};
    released
      .map((f) => ({ f, count: voteCountFor(f, false) }))
      .sort((a, b) => b.count - a.count || a.f.id - b.f.id)
      .forEach((r, i) => { rankNow[r.f.id] = i + 1; });

    const rankBefore = {};
    released
      .filter((f) => voteCountFor(f, true) > 0)
      .map((f) => ({ f, count: voteCountFor(f, true) }))
      .sort((a, b) => b.count - a.count || a.f.id - b.f.id)
      .forEach((r, i) => { rankBefore[r.f.id] = i + 1; });

    return released
      .filter((f) => rankBefore[f.id] != null)
      .map((f) => ({ name: f.name, delta: rankBefore[f.id] - rankNow[f.id] }))
      .filter((r) => r.delta !== 0)
      .sort((a, b) => b.delta - a.delta);
  }

  function renderBiggestMovers() {
    const root = $("#lb-biggest-movers");
    if (!root) return;
    const movers = computeMovement();

    root.innerHTML = "";
    if (!movers.length) {
      root.appendChild(el("p", { class: "text-dark/40 text-sm" }, "Nothing to report yet — check back soon."));
      return;
    }
    movers.forEach(({ name, delta }) => {
      const up = delta > 0;
      const label = (up ? "+" : "") + delta;
      root.appendChild(el("div", { class: "flex items-center justify-between gap-3" },
        el("span", { class: "text-dark font-semibold text-sm truncate" }, name),
        el("span", {
          class: "shrink-0 font-black text-sm tabular-nums px-2 py-0.5 rounded-full",
          style: `background: ${up ? "rgba(22,163,74,0.1)" : "rgba(220,38,38,0.1)"}; color: ${up ? "#16a34a" : "#dc2626"};`,
        }, label)
      ));
    });
  }

  /* ── "How it works" auto-advancing steps ─────────────────────────────── */
  function initHowSteps() {
    const buttons = $all(".lb-how-step");
    if (!buttons.length) return;
    let active = 0, paused = false;

    function apply() {
      buttons.forEach((btn, i) => {
        const on = i === active;
        const accent = btn.dataset.accent;
        const badge = btn.querySelector(".lb-how-badge");
        const kicker = btn.querySelector(".lb-how-kicker");
        badge.style.background = on ? accent : "#FFFFFF";
        badge.style.color = on ? btn.dataset.badgeText : accent;
        badge.style.boxShadow = on ? `0 14px 30px -10px ${accent}88` : "0 6px 16px -8px rgba(60,30,90,0.25)";
        badge.style.transform = on ? "scale(1.05)" : "scale(1)";
        kicker.style.opacity = on ? "1" : "0.5";
      });
    }
    buttons.forEach((btn, i) => {
      btn.addEventListener("click", () => { active = i; apply(); });
      btn.addEventListener("mouseenter", () => { active = i; apply(); paused = true; });
      btn.addEventListener("mouseleave", () => { paused = false; });
    });
    apply();
    setInterval(() => { if (!paused) { active = (active + 1) % buttons.length; apply(); } }, 2800);
  }

  /* ── Wire up static buttons (rally, scroll-to, toast dismiss, modal close) */
  function initStaticHandlers() {
    const rallyBtn = $("#lb-rally-btn");
    if (rallyBtn) rallyBtn.addEventListener("click", () => flash("🔗 Invite link copied — go rally the votes!"));

    $all("[data-scroll-to]").forEach((btn) => {
      btn.addEventListener("click", () => {
        const target = document.getElementById(btn.dataset.scrollTo);
        if (target) target.scrollIntoView({ behavior: "smooth", block: "start" });
      });
    });

    const toastClose = $("#lb-toast-close");
    if (toastClose) toastClose.addEventListener("click", hideToast);

    const modalBackdrop = $("#lb-modal-backdrop");
    if (modalBackdrop) {
      modalBackdrop.addEventListener("click", (e) => { if (e.target === modalBackdrop) VoteModal.close(); });
    }
    const modalClose = $("#lb-modal-close");
    if (modalClose) modalClose.addEventListener("click", () => VoteModal.close());
  }

  /* ── Main render (called on init + every Arena state change) ─────────── */
  let heroFeatured = null;
  function render() {
    heroFeatured = renderHero();
    renderStats();
    renderRankedList();
    renderHallOfFlavor();
    renderLiveFeed();
  }

  /* ── Boot ─────────────────────────────────────────────────────────────── */
  document.addEventListener("DOMContentLoaded", () => {
    Arena.hydrate();
    LiveFeed.init();
    Arena.onChange(render);

    render();
    initPostIt(heroFeatured);
    initCountdown();
    initHowSteps();
    initStaticHandlers();
    renderBiggestMovers();
    supabaseFetchMovement().then((data) => { movementData = data; renderBiggestMovers(); });

    const heroTiltRoot = $("#lb-hero-tilt");
    if (heroTiltRoot) attachTilt(heroTiltRoot, 12);

    // Background live tick — mirrors ArenaProvider's 450s interval
    const prefersReduced = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (!prefersReduced) setInterval(() => Arena.tick(), 450000);

    // Synthetic Grandma's Candy vote — see the comment above castSyntheticVote.
    if (!prefersReduced) setInterval(castSyntheticVote, 1800000);
  });
})();
